import { Button, Text, useComputedColorScheme } from '@mantine/core';
import { IconCaretRightFilled } from '@tabler/icons-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import Image from 'next/image';
import React, { useCallback, useMemo, useRef } from 'react';
import { AdUnitIncontent_1 } from '~/components/Ads/AdUnit';
import { AdUnitRenderable } from '~/components/Ads/AdUnitRenderable';
import { useAdsContext } from '~/components/Ads/AdsProvider';
import { useCreateAdFeed } from '~/components/Ads/ads.utils';
import type { MasonryRenderItemProps } from '~/components/MasonryColumns/masonry.types';
import { useMasonryContext } from '~/components/MasonryColumns/MasonryProvider';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import { useScrollAreaRef } from '~/components/ScrollArea/ScrollAreaContext';
import { useScrollMargin } from '~/hooks/useScrollMargin';
import { TwCard } from '~/components/TwCard/TwCard';
import { useBrowsingLevelDebounced } from '~/components/BrowsingLevel/BrowsingLevelProvider';
import { getIsSafeBrowsingLevel } from '~/shared/constants/browsingLevel.constants';
import { useIsMobile } from '~/hooks/useIsMobile';

// Matches the aspectRatioMap in ~/components/CardTemplates/AspectRatioCard.tsx.
// Values are width/height ratios, so rowHeight = columnWidth / cardAspectRatio.
const cardAspectRatioMap = {
  portrait: 7 / 9,
  landscape: 9 / 7,
  square: 1,
} as const;

type CardAspectRatio = keyof typeof cardAspectRatioMap;

type Props<TData> = {
  data: TData[];
  render: React.ComponentType<MasonryRenderItemProps<TData>>;
  itemId?: (data: TData) => string | number;
  empty?: React.ReactNode;
  withAds?: boolean;
  overscan?: number;
  /** Aspect ratio the card renders at. Must match the render component. Defaults to 'portrait' to match AspectRatioCard. */
  aspectRatio?: CardAspectRatio;
};

export function MasonryGridVirtual<TData>({
  data,
  render: RenderComponent,
  itemId,
  empty = null,
  withAds,
  overscan = 4,
  aspectRatio = 'portrait',
}: Props<TData>) {
  const colorScheme = useComputedColorScheme('dark');
  const { columnCount, columnWidth, columnGap, rowGap, maxSingleColumnWidth } = useMasonryContext();
  // Initial estimate only — the real row height is measured from the DOM via
  // `measureElement` below, so it stays correct during resize even before
  // MasonryProvider's debounced `columnCount` catches up.
  const estimatedRowHeight = Math.round(columnWidth / cardAspectRatioMap[aspectRatio]);

  const { adsEnabled, useDirectAds } = useAdsContext();
  const browsingLevel = useBrowsingLevelDebounced();
  const isMobile = useIsMobile();
  // Programmatic ads (civitai.com) render on all devices; direct ads (civitai.red) are mobile-only.
  const adsReallyAreEnabled =
    adsEnabled && getIsSafeBrowsingLevel(browsingLevel) && withAds && (!useDirectAds || isMobile);
  const createAdFeed = useCreateAdFeed();
  // Only interleave ads when they'll actually render. Otherwise AdUnitRenderable
  // short-circuits to null and leaves visible empty cells in the pre-sliced rows
  // (MasonryGrid's flat auto-flow grid absorbs those gaps; a row-based virtualizer
  // can't).
  const items = useMemo(
    () =>
      createAdFeed({
        data,
        columnCount,
        options: adsReallyAreEnabled
          ? [
              {
                width: 300,
                height: 250,
                AdUnit: AdUnitIncontent_1,
              },
            ]
          : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnCount, data, adsReallyAreEnabled]
  );

  // Guard against `columnCount === 0` (MasonryProvider hasn't measured width yet,
  // common on back-nav when React Query cache is hot). Without this, `items.length / 0`
  // is `Infinity` and `useVirtualizer({ count: Infinity })` locks the main thread.
  const rowCount = columnCount > 0 ? Math.ceil(items.length / columnCount) : 0;

  const ref = useRef<HTMLDivElement | null>(null);
  const scrollAreaRef = useScrollAreaRef();

  const scrollMargin = useScrollMargin(ref);

  const getRowKey = useCallback(
    (rowIndex: number) => {
      const firstItem = items[rowIndex * columnCount];
      if (!firstItem) return rowIndex;
      if (firstItem.type === 'data') return itemId?.(firstItem.data) ?? rowIndex;
      return `row_${rowIndex}`;
    },
    [items, columnCount, itemId]
  );

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollAreaRef?.current ?? null,
    estimateSize: () => estimatedRowHeight,
    overscan,
    getItemKey: getRowKey,
    gap: rowGap,
    scrollMargin,
    initialOffset: () => scrollAreaRef?.current?.scrollTop ?? 0,
    measureElement: (el) => el.getBoundingClientRect().height,
    // Opt into the native `scrollend` event so virtual-core skips its
    // internal 150ms setTimeout-based scroll-end debounce. That debounce was
    // installing ~38 timers/sec during feed scroll (perf audit 2026-04).
    // Falls back to the debounce automatically on browsers without scrollend.
    useScrollendEvent: true,
  });

  if (!items.length) {
    return <div style={{ height: columnWidth }}>{empty}</div>;
  }

  const gridTemplateColumns =
    columnCount === 1
      ? `minmax(${columnWidth}px, ${maxSingleColumnWidth ?? columnWidth}px)`
      : `repeat(${columnCount}, ${columnWidth}px)`;

  return (
    <div
      ref={ref}
      style={{
        height: rowVirtualizer.getTotalSize(),
        position: 'relative',
        width: '100%',
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const startIdx = virtualRow.index * columnCount;
        const rowItems = items.slice(startIdx, startIdx + columnCount);

        return (
          <div
            key={`${virtualRow.index}_${virtualRow.key}`}
            ref={rowVirtualizer.measureElement}
            data-index={virtualRow.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'grid',
              justifyContent: 'center',
              gridTemplateColumns,
              columnGap,
              transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
            }}
          >
            {rowItems.map((item, cellIdx) => {
              const index = startIdx + cellIdx;
              if (item.type === 'data') {
                const key = itemId?.(item.data) ?? index;
                return (
                  <RenderComponent
                    key={key}
                    index={index}
                    data={item.data}
                    width={columnWidth}
                    height={columnWidth}
                  />
                );
              }
              return (
                <AdUnitRenderable key={`ad_${index}`}>
                  <TwCard className="mx-auto min-w-80 justify-between gap-2 border p-2 shadow">
                    <div className="flex flex-col items-center gap-2">
                      <Image
                        src={`/images/logo_${colorScheme}_mode.png`}
                        alt="Civitai logo"
                        height={30}
                        width={142}
                      />
                      <Text>Become a Member to turn off ads today.</Text>
                      <Button
                        component={Link}
                        href="/pricing"
                        size="compact-sm"
                        color="green"
                        variant="outline"
                        className="w-24"
                      >
                        <Text fw={500}>Do It</Text>
                        <IconCaretRightFilled size={16} />
                      </Button>
                    </div>
                    <div>
                      <item.data.AdUnit />
                    </div>
                  </TwCard>
                </AdUnitRenderable>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
